import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Link2, ArrowLeft, Plus, Trash2, Copy, Check, ExternalLink, 
  Instagram, Facebook, Youtube, Twitter, Globe, Phone, Mail,
  Linkedin, MessageCircle, GripVertical, Eye
} from 'lucide-react';
import { toast } from 'sonner';
import { useAnalytics } from '../../utils/useAnalytics';

const socialIcons = {
  instagram: Instagram,
  facebook: Facebook,
  youtube: Youtube,
  twitter: Twitter,
  linkedin: Linkedin,
  whatsapp: MessageCircle,
  website: Globe,
  phone: Phone,
  email: Mail,
  other: Link2
};

const defaultLinks = [
  { id: 1, title: 'My Website', url: '', type: 'website', enabled: true },
  { id: 2, title: 'WhatsApp', url: '', type: 'whatsapp', enabled: true },
  { id: 3, title: 'Instagram', url: '', type: 'instagram', enabled: true },
];

const BioLinkGenerator = () => {
  const [profile, setProfile] = useState({
    name: '',
    bio: '',
    avatar: '',
    theme: 'dark'
  });
  const [links, setLinks] = useState(defaultLinks);
  const [copied, setCopied] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const { trackView, trackGenerate, trackDownload } = useAnalytics('bio-link');

  // Track page view
  useEffect(() => {
    trackView();
  }, [trackView]);

  const themes = [
    { id: 'dark', name: 'Dark', bg: 'bg-gray-900', text: 'text-white', button: 'bg-white/10 hover:bg-white/20' },
    { id: 'light', name: 'Light', bg: 'bg-gray-100', text: 'text-gray-900', button: 'bg-gray-900/10 hover:bg-gray-900/20' },
    { id: 'gradient', name: 'Gradient', bg: 'bg-gradient-to-br from-purple-600 to-pink-600', text: 'text-white', button: 'bg-white/20 hover:bg-white/30' },
    { id: 'neon', name: 'Neon', bg: 'bg-black', text: 'text-green-400', button: 'bg-green-500/20 hover:bg-green-500/30 border border-green-500' },
    { id: 'ocean', name: 'Ocean', bg: 'bg-gradient-to-br from-blue-600 to-teal-500', text: 'text-white', button: 'bg-white/20 hover:bg-white/30' },
  ];

  const addLink = () => {
    const newLink = {
      id: Date.now(),
      title: 'New Link',
      url: '',
      type: 'other',
      enabled: true
    };
    setLinks([...links, newLink]);
  };

  const updateLink = (id, field, value) => {
    setLinks(links.map(link => 
      link.id === id ? { ...link, [field]: value } : link
    ));
  };

  const removeLink = (id) => {
    setLinks(links.filter(link => link.id !== id));
  };

  const toggleLink = (id) => {
    setLinks(links.map(link =>
      link.id === id ? { ...link, enabled: !link.enabled } : link
    ));
  };

  const generateHTML = () => {
    const currentTheme = themes.find(t => t.id === profile.theme) || themes[0];
    const enabledLinks = links.filter(l => l.enabled && l.url);
    
    return `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${profile.name || 'My Bio Link'}</title>
    <link href="https://cdn.jsdelivr.net/npm/tailwindcss@2.2.19/dist/tailwind.min.css" rel="stylesheet">
    <style>
        body { min-height: 100vh; }
        .link-btn { transition: all 0.2s; }
        .link-btn:hover { transform: translateY(-2px); }
    </style>
</head>
<body class="${currentTheme.bg} ${currentTheme.text} min-h-screen p-6">
    <div class="max-w-md mx-auto pt-8">
        ${profile.avatar ? `<img src="${profile.avatar}" alt="${profile.name}" class="w-24 h-24 rounded-full mx-auto mb-4 object-cover border-4 border-white/20">` : 
        `<div class="w-24 h-24 rounded-full mx-auto mb-4 bg-white/20 flex items-center justify-center text-3xl font-bold">${profile.name?.charAt(0) || '?'}</div>`}
        <h1 class="text-2xl font-bold text-center mb-2">${profile.name || 'Your Name'}</h1>
        <p class="text-center opacity-80 mb-8">${profile.bio || 'Your bio here'}</p>
        <div class="space-y-4">
            ${enabledLinks.map(link => `
            <a href="${link.url}" target="_blank" rel="noopener" class="link-btn block w-full py-4 px-6 ${currentTheme.button} rounded-xl text-center font-medium">
                ${link.title}
            </a>`).join('')}
        </div>
        <p class="text-center text-sm opacity-50 mt-12">Created with ANANT Web Portal</p>
    </div>
</body>
</html>`;
  };

  const copyHTML = async () => {
    try {
      await navigator.clipboard.writeText(generateHTML());
      setCopied(true);
      trackGenerate({ theme: profile.theme, linksCount: links.filter(l => l.enabled).length });
      toast.success('HTML code copied! Paste in your website hosting.');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy');
    }
  };

  const downloadHTML = () => {
    const blob = new Blob([generateHTML()], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${profile.name?.toLowerCase().replace(/\s+/g, '-') || 'bio-link'}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    trackDownload({ theme: profile.theme });
    toast.success('HTML file downloaded!');
  };

  const currentTheme = themes.find(t => t.id === profile.theme) || themes[0];

  return (
    <div className="min-h-screen bg-background" data-testid="bio-link-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-pink-500/20 via-background to-purple-500/10 py-8 px-4">
        <div className="max-w-5xl mx-auto">
          <Link to="/tools" className="inline-flex items-center gap-2 text-muted-foreground hover:text-white mb-4">
            <ArrowLeft className="w-4 h-4" />
            Back to Tools
          </Link>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-gradient-to-r from-pink-500 to-purple-500 rounded-2xl flex items-center justify-center">
              <Link2 className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Bio Link Generator</h1>
              <p className="text-muted-foreground">Create a beautiful link-in-bio page</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Editor */}
          <div className="space-y-6">
            {/* Profile Section */}
            <div className="glass-card p-6 space-y-4">
              <h3 className="font-semibold">Profile</h3>
              <input
                type="text"
                placeholder="Your Name"
                value={profile.name}
                onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
                data-testid="name-input"
              />
              <textarea
                placeholder="Your bio or tagline"
                value={profile.bio}
                onChange={(e) => setProfile({ ...profile, bio: e.target.value })}
                rows={2}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl resize-none"
              />
              <input
                type="url"
                placeholder="Avatar URL (optional)"
                value={profile.avatar}
                onChange={(e) => setProfile({ ...profile, avatar: e.target.value })}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
              />
            </div>

            {/* Theme Selection */}
            <div className="glass-card p-6">
              <h3 className="font-semibold mb-4">Theme</h3>
              <div className="grid grid-cols-5 gap-2">
                {themes.map((theme) => (
                  <button
                    key={theme.id}
                    onClick={() => setProfile({ ...profile, theme: theme.id })}
                    className={`p-3 rounded-xl ${theme.bg} ${
                      profile.theme === theme.id ? 'ring-2 ring-primary ring-offset-2 ring-offset-background' : ''
                    }`}
                    title={theme.name}
                  >
                    <span className={`text-xs ${theme.text}`}>{theme.name}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Links */}
            <div className="glass-card p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold">Links</h3>
                <button
                  onClick={addLink}
                  className="px-3 py-1.5 bg-primary/20 text-primary rounded-lg text-sm flex items-center gap-1 hover:bg-primary/30"
                >
                  <Plus className="w-4 h-4" />
                  Add Link
                </button>
              </div>

              <div className="space-y-3">
                {links.map((link) => {
                  const Icon = socialIcons[link.type] || Link2;
                  return (
                    <div key={link.id} className="p-4 bg-white/5 rounded-xl space-y-3">
                      <div className="flex items-center gap-3">
                        <GripVertical className="w-5 h-5 text-muted-foreground cursor-grab" />
                        <select
                          value={link.type}
                          onChange={(e) => updateLink(link.id, 'type', e.target.value)}
                          className="px-3 py-2 bg-white/10 border border-white/10 rounded-lg text-sm"
                        >
                          {Object.keys(socialIcons).map((type) => (
                            <option key={type} value={type}>{type.charAt(0).toUpperCase() + type.slice(1)}</option>
                          ))}
                        </select>
                        <input
                          type="text"
                          placeholder="Link Title"
                          value={link.title}
                          onChange={(e) => updateLink(link.id, 'title', e.target.value)}
                          className="flex-1 px-3 py-2 bg-white/10 border border-white/10 rounded-lg"
                        />
                        <button
                          onClick={() => toggleLink(link.id)}
                          className={`p-2 rounded-lg ${link.enabled ? 'bg-green-500/20 text-green-500' : 'bg-white/10 text-muted-foreground'}`}
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => removeLink(link.id)}
                          className="p-2 text-red-500 hover:bg-red-500/20 rounded-lg"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      <input
                        type="url"
                        placeholder="https://..."
                        value={link.url}
                        onChange={(e) => updateLink(link.id, 'url', e.target.value)}
                        className="w-full px-3 py-2 bg-white/10 border border-white/10 rounded-lg text-sm"
                      />
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-4">
              <button
                onClick={copyHTML}
                className="flex-1 py-4 bg-primary text-primary-foreground rounded-xl font-semibold flex items-center justify-center gap-2 hover:bg-primary/90"
                data-testid="copy-html-btn"
              >
                {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                {copied ? 'Copied!' : 'Copy HTML'}
              </button>
              <button
                onClick={downloadHTML}
                className="flex-1 py-4 bg-white/10 rounded-xl font-semibold flex items-center justify-center gap-2 hover:bg-white/20"
                data-testid="download-html-btn"
              >
                <ExternalLink className="w-5 h-5" />
                Download HTML
              </button>
            </div>
          </div>

          {/* Preview */}
          <div className="lg:sticky lg:top-8">
            <div className="glass-card p-4 mb-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold">Live Preview</h3>
                <button
                  onClick={() => setShowPreview(!showPreview)}
                  className="lg:hidden px-3 py-1 bg-white/10 rounded-lg text-sm"
                >
                  {showPreview ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>
            
            <div className={`${showPreview ? 'block' : 'hidden lg:block'}`}>
              <div className={`rounded-3xl overflow-hidden ${currentTheme.bg} p-6 min-h-[600px]`}>
                <div className="max-w-xs mx-auto pt-8">
                  {profile.avatar ? (
                    <img 
                      src={profile.avatar} 
                      alt={profile.name} 
                      className="w-24 h-24 rounded-full mx-auto mb-4 object-cover border-4 border-white/20"
                    />
                  ) : (
                    <div className="w-24 h-24 rounded-full mx-auto mb-4 bg-white/20 flex items-center justify-center text-3xl font-bold">
                      <span className={currentTheme.text}>{profile.name?.charAt(0) || '?'}</span>
                    </div>
                  )}
                  <h2 className={`text-2xl font-bold text-center mb-2 ${currentTheme.text}`}>
                    {profile.name || 'Your Name'}
                  </h2>
                  <p className={`text-center opacity-80 mb-8 ${currentTheme.text}`}>
                    {profile.bio || 'Your bio here'}
                  </p>
                  <div className="space-y-4">
                    {links.filter(l => l.enabled).map((link) => {
                      const Icon = socialIcons[link.type] || Link2;
                      return (
                        <button
                          key={link.id}
                          className={`w-full py-4 px-6 ${currentTheme.button} rounded-xl ${currentTheme.text} font-medium flex items-center justify-center gap-2 transition-all hover:-translate-y-0.5`}
                        >
                          <Icon className="w-5 h-5" />
                          {link.title}
                        </button>
                      );
                    })}
                  </div>
                  <p className={`text-center text-sm opacity-50 mt-12 ${currentTheme.text}`}>
                    Created with ANANT Web Portal
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BioLinkGenerator;
